import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Stack, Typography, useMediaQuery } from "@mui/material"
import fp from "lodash/fp"

import type { Theme } from "@mui/material"
import type { Conductor } from "types/conductors.types"
import type { Powerline } from "types/powerlines.types"
import type { SimpleTableColumn } from "widgets/common/SimpleTable"

import CardWithTitle from "widgets/common/CardWithTitle"
import Chip from "widgets/common/Chip"
import Message from "widgets/common/Message"
import SimpleTable from "widgets/common/SimpleTable"
import usePowerlineConductors from "helpers/hooks/conductors/usePowerlineConductors"
import {
  conductorEnumLabel,
  conductorName,
  formatConductorLength,
  formatConductorSection,
} from "helpers/utils/conductors"
import ConductorMobileList from "./ConductorMobileList"
import ConductorSpecDialog from "./ConductorSpecDialog"

interface ConductorsCardProps {
  powerline: Powerline
}

const ConductorsCard = ({ powerline }: ConductorsCardProps) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage ?? "en"
  const { conductors, conductorIds, isLoading } = usePowerlineConductors({ powerline })
  const [opened, setOpened] = useState<Conductor | undefined>()
  // One shape or the other, never both: hiding one with CSS would leave its
  // content in the page twice, for a screen reader to read out twice.
  const isWide = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"), {
    defaultMatches: true,
  })

  const columns: SimpleTableColumn<Conductor>[] = useMemo(
    () => [
      {
        key: "type",
        labelKey: "conductors.TYPE",
        width: "35%",
        render: (conductor) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Chip label={conductor.type} />
            {/* The code alone says little, so the type is spelled out beside it. */}
            <Typography variant="body2" color="neutral.600">
              {conductorEnumLabel(t, "TYPES", conductor.type)}
            </Typography>
          </Stack>
        ),
      },
      {
        key: "name",
        labelKey: "conductors.NAME",
        width: "25%",
        render: (conductor) => (
          <>
            <Typography fontStyle={conductor.name ? "normal" : "italic"}>
              {conductorName(t, conductor)}
            </Typography>
            <Typography variant="body2" color="neutral.600">
              {conductor.manufacturer ?? "-"}
            </Typography>
          </>
        ),
      },
      {
        key: "section",
        labelKey: "conductors.NOMINAL_SECTION",
        width: "20%",
        render: (conductor) =>
          fp.isNil(conductor.nominal_section)
            ? "-"
            : formatConductorSection(conductor.nominal_section, locale),
      },
      {
        key: "diameter",
        labelKey: "conductors.DIAMETER",
        width: "20%",
        render: (conductor) => formatConductorLength(conductor.diameter, locale),
      },
    ],
    [t, locale],
  )

  const count = conductorIds.length

  return (
    <CardWithTitle titleKey="conductors.CONDUCTORS">
      {count === 0 ? (
        <Message
          messageKey="conductors.NO_CONDUCTORS"
          containerStyles={{ height: "100%", minHeight: "120px" }}
        />
      ) : isWide ? (
        <SimpleTable
          columns={columns}
          rows={conductors}
          getRowKey={(conductor) => conductor.id}
          // The line names its conductors before they load, so the wait is sized.
          loadingRows={isLoading ? count : 0}
          onRowClick={setOpened}
        />
      ) : (
        <ConductorMobileList
          conductors={conductors}
          loadingCount={isLoading ? count : 0}
          onSelect={setOpened}
        />
      )}
      <ConductorSpecDialog conductor={opened} onClose={() => setOpened(undefined)} />
    </CardWithTitle>
  )
}

export default ConductorsCard
